import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../trpc';
import { useUser } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { FeedItemCard } from '@/components/feed/FeedItemCard';
import { FeedSectionHeader } from '@/components/feed/FeedSectionHeader';
import { waitForClerk } from '@/utils/clerk';
import { getFeedSectionKey, compareSectionKeys } from '@/utils/feedDateUtils';
import { FeedItemType } from 'types';

export const Route = createFileRoute('/dashboard/feed')({
  component: FeedContent,
  beforeLoad: async () => {
    const user = await waitForClerk();
    if (!user) {
      return redirect({
        to: '/',
        replace: true,
      });
    }
  },
});

function FeedContent() {
  const { t } = useTranslation();
  const { user } = useUser();
  const userId = user?.id;
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Get feed items with infinite scroll
  const feedQuery = trpc.feed.getFeed.useInfiniteQuery(
    {
      limit: 20,
    },
    {
      getNextPageParam: (lastPage: any) => lastPage.nextCursor,
      enabled: !!userId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30000, // 30 seconds
      refetchInterval: 60000, // 60 second polling for near real-time
      retry: 2,
      retryDelay: 500,
      onError: (error: Error) => {
        console.error('Feed query error:', error);
        Sentry.captureException(error, {
          tags: {
            component: 'FeedContent',
            action: 'getFeed',
            userId,
          },
        });
      },
    },
  );

  // Get unread counts (for future use in feed filters/badges)
  trpc.feed.getUnreadCounts.useQuery(
    {},
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
      refetchInterval: 60000, // Match feed polling interval
      onError: (error: Error) => {
        Sentry.captureException(error, {
          tags: {
            component: 'FeedContent',
            action: 'getUnreadCounts',
            userId,
          },
        });
      },
    },
  );

  // Mark as read mutation
  const markAsReadMutation = trpc.feed.markAsRead.useMutation({
    onSuccess: () => {
      feedQuery.refetch();
    },
    onError: (error: Error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'FeedContent',
          action: 'markAsRead',
          userId,
        },
      });
    },
  });

  // Handle marking item as read
  const handleMarkAsRead = useCallback(
    (itemId: string, feedType: FeedItemType) => {
      markAsReadMutation.mutate({
        itemIds: [itemId],
        feedType,
      });
    },
    [markAsReadMutation],
  );

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current || feedQuery.isFetching) return;

    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && feedQuery.hasNextPage) {
        feedQuery.fetchNextPage();
      }
    });

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [feedQuery]);

  // Flatten pages into single array
  const feedItems = feedQuery.data?.pages.flatMap((page: any) => page.items) ?? [];

  // Group items by time period
  const groupedItems = useMemo(() => {
    const groups: Record<string, any[]> = {};

    feedItems.forEach((item: any) => {
      const sectionKey = getFeedSectionKey(item.createdAt);
      if (!groups[sectionKey]) {
        groups[sectionKey] = [];
      }
      groups[sectionKey].push(item);
    });

    // Sort sections by time order (newest to oldest)
    const sortedKeys = Object.keys(groups).sort(compareSectionKeys);

    return sortedKeys.map((key) => ({
      sectionKey: key,
      items: groups[key],
    }));
  }, [feedItems]);

  return (
    <div className="h-[calc(100vh-200px)] overflow-y-auto">
      {/* Feed Items */}
      <div>
        {feedQuery.isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : feedQuery.error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              {t('common.genericError', 'Something went wrong. Please try again.')}
            </p>
          </div>
        ) : feedItems.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <p className="text-gray-600">
              {t(
                'feed.empty',
                'Your feed is empty. Check back soon for updates!',
              )}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-0">
              {groupedItems.map((section) => (
                <div key={section.sectionKey}>
                  <div className="sticky top-0 z-10">
                    <FeedSectionHeader sectionKey={section.sectionKey} />
                  </div>
                  <div className="space-y-4 p-4">
                    {section.items.map((item: any) => (
                      <FeedItemCard
                        key={`${item.feedType}-${item.id}`}
                        item={item}
                        onMarkAsRead={handleMarkAsRead}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Infinite scroll sentinel */}
            {feedQuery.hasNextPage && (
              <div ref={sentinelRef} className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            )}

            {!feedQuery.hasNextPage && feedItems.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {t('feed.endOfFeed', 'You\'ve reached the end of your feed')}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
